import { formatChannel } from '../lib/format.js';

/**
 * Channel pill. A small dot (accent-colored to identify the channel)
 * plus the channel name in mixed case. Background is a subtle tint
 * that picks up via the remapped `*-950` token so contrast is gentle
 * but the badge is still scannable in a dense row.
 */
const STYLES: Record<string, { bg: string; text: string; ring: string; dot: string }> = {
  linkedin: {
    bg: 'bg-sky-950',
    text: 'text-sky-200',
    ring: 'ring-sky-900',
    dot: 'bg-sky-400',
  },
  whatsapp: {
    bg: 'bg-emerald-950',
    text: 'text-emerald-200',
    ring: 'ring-emerald-900',
    dot: 'bg-emerald-400',
  },
  email: {
    bg: 'bg-amber-950',
    text: 'text-amber-200',
    ring: 'ring-amber-900',
    dot: 'bg-amber-400',
  },
  telegram: {
    bg: 'bg-indigo-950',
    text: 'text-indigo-200',
    ring: 'ring-indigo-900',
    dot: 'bg-indigo-400',
  },
  instagram: {
    bg: 'bg-fuchsia-950',
    text: 'text-fuchsia-200',
    ring: 'ring-fuchsia-900',
    dot: 'bg-fuchsia-400',
  },
  messenger: {
    bg: 'bg-blue-950',
    text: 'text-blue-200',
    ring: 'ring-blue-900',
    dot: 'bg-blue-400',
  },
  unknown: {
    bg: 'bg-zinc-900',
    text: 'text-zinc-400',
    ring: 'ring-zinc-800',
    dot: 'bg-zinc-500',
  },
};

interface Props {
  channel: string;
  size?: 'sm' | 'xs';
}

export function ChannelBadge({ channel, size = 'sm' }: Props) {
  const style = STYLES[channel] ?? STYLES.unknown;
  // biome-ignore lint/style/noNonNullAssertion: unknown branch always defined
  const s = style!;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md font-medium ring-1 ring-inset ${s.bg} ${s.text} ${s.ring} ${
        size === 'sm' ? 'px-1.5 py-0.5 text-[10px]' : 'px-1 py-px text-[9px]'
      }`}
    >
      <span aria-hidden="true" className={`inline-block h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {formatChannel(channel)}
    </span>
  );
}
